import { useEffect, useMemo, useState } from 'react';
import { atlasService, type AtlasNode } from '../services/atlas.service.js';
import { useAuthStore } from '../stores/authStore.js';

/**
 * Module entries surface from atlas node metadata (roadmap #43).
 * Rather than hardcoding routes/icons in the frontend, the dashboard +
 * sidebar both read from this hook so they stay in sync with atlas.
 */
export interface ModuleEntry {
  id: number;
  slug: string;
  title: string;
  description: string;
  routePath: string;
  iconName: string;
  requiredRole?: string;
  /** Permission slugs the user must hold to access this module (#98). */
  requiredPermissions?: string[];
  showOnDashboard: boolean;
  dashboardOrder: number;
  /** ISO string from atlas — surface for "X dakika önce" badge */
  updatedAt: string;
}

interface UseModulesOptions {
  /** Apply current user's role filter. Defaults to true. */
  filterByRole?: boolean;
  /**
   * Apply current user's permission filter against module.requiredPermissions
   * (#98). Defaults to true. Admin role automatically passes everything.
   */
  filterByPermissions?: boolean;
  /** Restrict to modules whose metadata.showOnDashboard === true. */
  dashboardOnly?: boolean;
}

interface UseModulesResult {
  modules: ModuleEntry[];
  loading: boolean;
  error: string | null;
}

function nodeToModule(node: AtlasNode): ModuleEntry | null {
  const meta = (node.metadata ?? {}) as Record<string, unknown>;
  const routePath = typeof meta.routePath === 'string' ? meta.routePath : null;
  const iconName = typeof meta.iconName === 'string' ? meta.iconName : null;
  const description = typeof meta.description === 'string' ? meta.description : '';
  if (!routePath || !iconName) return null; // backend warned; skip silently
  const reqPerms = Array.isArray(meta.requiredPermissions)
    ? (meta.requiredPermissions as unknown[]).filter((s): s is string => typeof s === 'string')
    : undefined;
  return {
    id: node.id,
    slug: node.slug,
    title: node.title,
    description,
    routePath,
    iconName,
    requiredRole: typeof meta.requiredRole === 'string' ? meta.requiredRole : undefined,
    requiredPermissions: reqPerms,
    showOnDashboard: meta.showOnDashboard !== false,
    dashboardOrder: typeof meta.dashboardOrder === 'number' ? meta.dashboardOrder : 999,
    updatedAt: node.updatedAt,
  };
}

export function useModules(options: UseModulesOptions = {}): UseModulesResult {
  const { filterByRole = true, filterByPermissions = true, dashboardOnly = false } = options;
  const role = useAuthStore((s) => s.user?.role);
  const userPerms = useAuthStore((s) => s.user?.permissions);
  const [nodes, setNodes] = useState<AtlasNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    atlasService
      .listNodes({ type: 'module', perPage: 100, sortBy: 'updated_at', sortOrder: 'desc' })
      .then((res) => {
        if (cancelled) return;
        setNodes(res.data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const modules = useMemo(() => {
    const out: ModuleEntry[] = [];
    const isAdmin = role === 'admin';
    const permSet = new Set(userPerms ?? []);
    for (const n of nodes) {
      const m = nodeToModule(n);
      if (!m) continue;
      if (dashboardOnly && !m.showOnDashboard) continue;
      if (filterByRole && m.requiredRole && m.requiredRole !== role) continue;
      // #98 — permission gate. Admin role bypasses (matches backend
      // ROLE_DEFAULT_PERMISSIONS = ['*']). Otherwise require every listed
      // requiredPermissions slug to be in the user's effective set.
      if (
        filterByPermissions &&
        !isAdmin &&
        m.requiredPermissions &&
        m.requiredPermissions.length > 0 &&
        !m.requiredPermissions.every((p) => permSet.has(p))
      ) {
        continue;
      }
      out.push(m);
    }
    out.sort((a, b) => a.dashboardOrder - b.dashboardOrder);
    return out;
  }, [nodes, role, userPerms, filterByRole, filterByPermissions, dashboardOnly]);

  return { modules, loading, error };
}
